import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { FaCheckCircle, FaHeart, FaClock } from 'react-icons/fa';
import { Button } from '@/components/ui/button';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Pantalla "Tu plan está siendo preparado" - 24h de espera diferida
 * de Fit Evolution. Muestra countdown y mensaje personalizado por género.
 */
export default function FitEvolutionPreparing({ user }) {
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchStatus = async () => {
    if (!user?.email) { setLoading(false); return; }
    try {
      // Timeout agresivo (3s) para no atascar la UI si el backend tarda.
      // Si expira, salimos del loading y mostramos defaults optimistas (24h).
      const res = await axios.get(
        `${API}/api/personalized-plans/fit-evolution/plan-ready-status/${encodeURIComponent(user.email)}`,
        { timeout: 3000 }
      );
      setStatus(res.data);
    } catch (e) { console.error('plan-ready-status:', e?.message || e); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    fetchStatus();
    // Poll cada 60 segundos por si el admin publica antes o pasan las 24h
    const iv = setInterval(fetchStatus, 60000);
    return () => clearInterval(iv);
  }, [user]);

  if (loading) return (
    <div className="min-h-screen py-8 px-4 md:px-6 flex items-center" style={{ backgroundColor: '#0a0a0a' }} data-testid="fit-evo-preparing-loading">
      <div className="fixed inset-0 z-0 pointer-events-none" style={{ background: 'radial-gradient(circle at 15% 85%, rgba(var(--brand-primary-glow),0.18), transparent 55%), radial-gradient(circle at 85% 15%, rgba(0,240,255,0.12), transparent 45%)' }} />
      <div className="max-w-2xl mx-auto relative z-10 text-center w-full">
        <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }}>
          <p className="text-[11px] font-bold uppercase tracking-[0.3em] mb-3" style={{ color: 'var(--brand-primary)' }}>Fit Evolution</p>
          <motion.div
            animate={{ scale: [1, 1.06, 1] }}
            transition={{ duration: 1.6, repeat: Infinity, ease: 'easeInOut' }}
            className="text-6xl md:text-7xl mb-4"
          >
            💫
          </motion.div>
          <h1 className="text-2xl md:text-4xl font-black text-white mb-3 uppercase tracking-tight" style={{ fontFamily: 'Poppins, sans-serif' }}>
            Procesando{' '}
            <span className="bg-clip-text text-transparent" style={{ backgroundImage: 'linear-gradient(135deg, var(--brand-primary), #00F0FF)' }}>formulario</span>
          </h1>
          <p className="text-gray-300 text-sm md:text-base max-w-md mx-auto mb-6 leading-relaxed">
            Estamos guardando tu información. Por favor, no cierres la ventana.
          </p>
          <div className="flex items-center justify-center gap-2 text-pink-300/80 text-xs">
            <span className="w-2 h-2 rounded-full bg-pink-400 animate-pulse" style={{ animationDelay: '0ms' }} />
            <span className="w-2 h-2 rounded-full bg-pink-400 animate-pulse" style={{ animationDelay: '150ms' }} />
            <span className="w-2 h-2 rounded-full bg-pink-400 animate-pulse" style={{ animationDelay: '300ms' }} />
          </div>
        </motion.div>
      </div>
    </div>
  );

  // Si el plan ya está listo → ir al dashboard
  if (status?.status === 'ready') {
    return (
      <div className="min-h-screen flex items-center justify-center px-4" style={{ backgroundColor: '#0a0a0a' }}>
        <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
          className="max-w-md text-center rounded-3xl p-8" style={{ background: 'linear-gradient(135deg, rgba(34,197,94,0.15), rgba(16,185,129,0.05))', border: '1px solid rgba(34,197,94,0.4)' }}>
          <FaCheckCircle className="text-6xl text-green-400 mx-auto mb-4" />
          <p className="text-[11px] uppercase tracking-widest text-green-300 font-bold mb-2">Fit Evolution</p>
          <h2 className="text-2xl font-black text-white mb-3">¡Tu plan ya está listo!</h2>
          <p className="text-white/80 mb-6">Ya puedes acceder a tu plan personalizado de alimentación y entrenamiento.</p>
          <Button onClick={() => navigate('/dashboard')} className="rounded-full font-bold text-white px-8 py-6" style={{ background: 'linear-gradient(135deg, var(--brand-primary), var(--brand-primary-strong))' }} data-testid="go-to-dashboard">
            Entrar a mi plan
          </Button>
        </motion.div>
      </div>
    );
  }

  const awaitingPhotos = status?.status === 'awaiting_photos';

  const gender = status?.gender || user?.gender || 'F';
  // Aceptar cualquier variante que denote femenino: F, female, mujer, femenino, f.
  // Fallback: si el nombre termina en 'a' (heurística), asumir femenino.
  const genderNorm = String(gender || '').trim().toLowerCase();
  const nameFirst = (user?.name || '').trim().split(' ')[0] || '';
  const isF = ['f', 'female', 'mujer', 'femenino', 'femenina'].includes(genderNorm)
    || (!['m', 'male', 'hombre', 'masculino'].includes(genderNorm) && /[aá]$/i.test(nameFirst));
  const welcomeName = nameFirst || (isF ? 'Guerrera' : 'Guerrero');
  const bienvenido = isF ? 'Bienvenida' : 'Bienvenido';
  const adaptado = 'adaptado';

  const hoursLeft = status?.hours_left ?? 24;
  const minutesLeft = status?.minutes_left ?? 0;

  return (
    <div className="min-h-screen py-8 px-4 md:px-6 flex items-center" style={{ backgroundColor: '#0a0a0a' }} data-testid="fit-evo-preparing">
      <div className="fixed inset-0 z-0 pointer-events-none" style={{ background: 'radial-gradient(circle at 15% 85%, rgba(var(--brand-primary-glow),0.18), transparent 55%), radial-gradient(circle at 85% 15%, rgba(0,240,255,0.12), transparent 45%)' }} />

      <div className="max-w-2xl mx-auto relative z-10 text-center">
        <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }}>
          <p className="text-[11px] font-bold uppercase tracking-[0.3em] mb-3" style={{ color: 'var(--brand-primary)' }}>Fit Evolution</p>

          <motion.div animate={{ rotate: [0, 5, -5, 0] }} transition={{ duration: 3, repeat: Infinity }} className="text-6xl md:text-7xl mb-4">🎉</motion.div>

          <h1 className="text-3xl md:text-5xl font-black text-white mb-4 uppercase tracking-tight" style={{ fontFamily: 'Poppins, sans-serif' }}>
            ¡{bienvenido} a{' '}
            <span className="bg-clip-text text-transparent" style={{ backgroundImage: 'linear-gradient(135deg, var(--brand-primary), #00F0FF)' }}>Fit Evolution</span>{welcomeName ? `, ${welcomeName}` : ''}!
          </h1>

          <p className="text-gray-200 text-base md:text-lg max-w-lg mx-auto mb-6 leading-relaxed">
            Tu coach ya está preparando tu plan personalizado de alimentación y entrenamiento teniendo en cuenta toda la información que nos compartiste.
          </p>

          <p className="text-gray-300 text-sm md:text-base max-w-lg mx-auto mb-8">
            Queremos asegurarnos de que recibas un plan {adaptado} a tu objetivo, preferencias y necesidades.
          </p>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}
          className="rounded-2xl p-5 mb-6 text-left flex flex-col sm:flex-row sm:items-center gap-4"
          style={{ background: 'rgba(250,204,21,0.10)', border: '1px solid rgba(250,204,21,0.45)' }}
          data-testid="fe-photos-reminder-badge">
          <div className="flex-1">
            <p className="text-yellow-300 font-bold text-sm uppercase tracking-wider mb-1">
              {awaitingPhotos ? 'Faltan tus fotos iniciales' : 'Fotos recibidas'}
            </p>
            <p className="text-white/90 text-sm leading-relaxed">
              {awaitingPhotos
                ? 'Recuerda que tu plan estará listo 24 horas después de que adjuntes tus fotos iniciales. ¡Adjúntalas ya!'
                : 'Recibimos tus fotos iniciales. Tu plan estará listo 24 horas después de haberlas adjuntado.'}
            </p>
          </div>
          {awaitingPhotos && (
            <Button onClick={() => navigate('/my-progress')} className="rounded-full font-bold text-black px-6"
              style={{ background: '#facc15' }} data-testid="fe-upload-photos-btn">
              Adjuntar mis fotos
            </Button>
          )}
        </motion.div>

        {/* Countdown card */}
        {!awaitingPhotos && (
        <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.2 }}
          className="rounded-3xl p-6 md:p-8 mb-6"
          style={{ background: 'linear-gradient(135deg, rgba(var(--brand-primary-glow),0.14), rgba(0,240,255,0.06))', border: '2px solid rgba(var(--brand-primary-glow),0.4)', boxShadow: '0 0 40px rgba(var(--brand-primary-glow),0.15)' }}>
          <p className="text-white/80 text-sm mb-3 flex items-center justify-center gap-2">
            <FaHeart className="text-pink-400 animate-pulse" />
            Tu plan estará disponible en un plazo máximo de
          </p>
          <div className="flex items-center justify-center gap-4 mb-2">
            <div className="text-center" data-testid="hours-left">
              <p className="text-5xl md:text-6xl font-black text-white" style={{ fontFamily: 'Poppins, sans-serif' }}>{hoursLeft}</p>
              <p className="text-[11px] uppercase tracking-widest text-pink-300 font-bold mt-1">horas</p>
            </div>
            <span className="text-4xl text-white/40">:</span>
            <div className="text-center" data-testid="minutes-left">
              <p className="text-5xl md:text-6xl font-black text-white" style={{ fontFamily: 'Poppins, sans-serif' }}>{minutesLeft.toString().padStart(2, '0')}</p>
              <p className="text-[11px] uppercase tracking-widest text-pink-300 font-bold mt-1">minutos</p>
            </div>
          </div>
          <p className="text-[11px] text-white/60 mt-4 italic flex items-center justify-center gap-1">
            <FaClock className="text-white/50" /> Te notificaremos apenas esté listo.
          </p>
        </motion.div>
        )}

        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4 }}>
          <p className="text-gray-400 text-sm mb-4">Mientras tanto, puedes explorar la aplicación y familiarizarte con todas las herramientas disponibles.</p>
          <Button onClick={() => navigate('/dashboard')} variant="outline" className="text-white/90 border-white/25 hover:bg-white/10 rounded-full px-8 py-6" data-testid="explore-app">
            Explorar la app
          </Button>
        </motion.div>
      </div>
    </div>
  );
}
